/**
 * /c/:slug/settings - tenant settings (CHAP-91 / Phase 1C #57).
 *
 * Team + admin only (server-gated via requireRole; client also hides it from
 * customers). Edit display name, chapter metadata, brand colors, digest toggle.
 */
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useTenantData } from '../hooks/useTenantData.js';
import { useUserRoles } from '../hooks/useUserRoles.js';
import { apiPatch, apiPost } from '../lib/api.js';

export default function TenantSettings() {
  const { slug = '' } = useParams();
  const qc = useQueryClient();
  const { allowedRoles } = useUserRoles();
  const canEdit = allowedRoles.includes('team') || allowedRoles.includes('admin');
  const { data, isLoading } = useTenantData(slug);
  const t = data?.tenant;

  const [form, setForm] = useState({
    displayName: '',
    university: '',
    chapterOrg: '',
    address: '',
    memberNumberLabel: '',
    brandAccent: '#cc785c',
    primaryColor: '#03244d',
    digestEnabled: true,
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!t) return;
    setForm({
      displayName: t.displayName ?? '',
      university: t.university ?? '',
      chapterOrg: t.chapterOrg ?? '',
      address: t.address ?? '',
      memberNumberLabel: t.memberNumberLabel ?? '',
      brandAccent: t.brandAccent ?? '#cc785c',
      primaryColor: t.primaryColor ?? '#03244d',
      digestEnabled: t.digestEnabled ?? true,
    });
  }, [t]);

  if (!canEdit) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 text-sm text-neutral">
        Settings are available to chapter staff (team/admin) only.
      </div>
    );
  }

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSaved(false);
    setSaving(true);
    try {
      await apiPatch(`/api/c/${slug}/settings`, {
        displayName: form.displayName.trim(),
        university: form.university.trim() || null,
        chapterOrg: form.chapterOrg.trim() || null,
        address: form.address.trim() || null,
        memberNumberLabel: form.memberNumberLabel.trim() || undefined,
        brandAccent: form.brandAccent,
        primaryColor: form.primaryColor,
        digestEnabled: form.digestEnabled,
      });
      await qc.invalidateQueries({ queryKey: ['tenant-config', slug] });
      setSaved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save.');
    } finally {
      setSaving(false);
    }
  }

  const upd = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [k]: k === 'digestEnabled' ? e.target.checked : e.target.value }));

  return (
    <div className="space-y-4 min-w-0 max-w-2xl">
      <header className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
        <h2 className="text-navy text-xl sm:text-2xl font-bold">Chapter settings</h2>
        <p className="text-neutral text-sm mt-1">Per CHAP-91. Team + admin only.</p>
      </header>

      {isLoading && <div className="bg-white rounded-xl border border-gray-100 p-6 text-sm text-neutral">Loading…</div>}

      {t && (
        <form onSubmit={save} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6 space-y-4 min-w-0">
          <L label="Display name"><input value={form.displayName} onChange={upd('displayName')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" /></L>
          <div className="grid sm:grid-cols-2 gap-4 min-w-0">
            <L label="University"><input value={form.university} onChange={upd('university')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" /></L>
            <L label="Chapter org"><input value={form.chapterOrg} onChange={upd('chapterOrg')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" /></L>
          </div>
          <L label="Address"><input value={form.address} onChange={upd('address')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" /></L>
          <div className="grid sm:grid-cols-3 gap-4 min-w-0">
            <L label="Member # label"><input value={form.memberNumberLabel} onChange={upd('memberNumberLabel')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" /></L>
            <L label="Brand accent"><input type="color" value={form.brandAccent} onChange={upd('brandAccent')} className="w-full h-9 border border-gray-300 rounded" /></L>
            <L label="Primary color"><input type="color" value={form.primaryColor} onChange={upd('primaryColor')} className="w-full h-9 border border-gray-300 rounded" /></L>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.digestEnabled} onChange={upd('digestEnabled')} />
            <span>Quarterly Cabinet progress digest enabled</span>
          </label>

          {error && <div className="border border-red-200 bg-red-50 text-red-800 text-sm rounded p-2">{error}</div>}
          {saved && <div className="border border-green-200 bg-green-50 text-green-800 text-sm rounded p-2">Saved.</div>}

          <div className="pt-2">
            <button type="submit" disabled={saving} className="px-4 py-2 rounded bg-navy text-white text-sm font-medium disabled:opacity-50">
              {saving ? 'Saving…' : 'Save settings'}
            </button>
          </div>
        </form>
      )}

      {t && <BillingCard slug={slug} />}
    </div>
  );
}

const PHASES: Array<{ key: string; label: string; price: string }> = [
  { key: 'silent', label: 'Silent Phase', price: '$499/mo' },
  { key: 'public', label: 'Public Phase', price: '$499/mo' },
  { key: 'construction', label: 'Construction Phase', price: '$249/mo' },
  { key: 'post_occupancy', label: 'Post-Occupancy', price: '$99/mo' },
];

function BillingCard({ slug }: { slug: string }) {
  const [lxa, setLxa] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function go(path: string, body?: unknown, tag?: string) {
    setError(null);
    setBusy(tag ?? path);
    try {
      const { url } = await apiPost<{ url: string }>(path, body);
      if (url) window.location.href = url;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Billing request failed.');
      setBusy(null);
    }
  }

  return (
    <section className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6 space-y-3 min-w-0">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-navy text-sm font-semibold">Billing</h3>
        <button
          type="button"
          onClick={() => go(`/api/c/${slug}/billing-portal`, undefined, 'portal')}
          disabled={busy !== null}
          className="text-sm px-3 py-1.5 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
        >
          {busy === 'portal' ? '…' : 'Manage billing'}
        </button>
      </div>
      <p className="text-xs text-neutral">Per-chapter subscription, billed monthly. Planning phase is free; Omega Zeta is complimentary.</p>
      <label className="flex items-center gap-2 text-xs text-neutral">
        <input type="checkbox" checked={lxa} onChange={(e) => setLxa(e.target.checked)} />
        Apply LXA partner discount (25% off)
      </label>
      <div className="grid sm:grid-cols-2 gap-2 min-w-0">
        {PHASES.map((p) => (
          <button
            key={p.key}
            type="button"
            onClick={() => go(`/api/c/${slug}/subscribe`, { phase: p.key, lxa }, p.key)}
            disabled={busy !== null}
            className="text-sm px-3 py-2 rounded bg-navy text-white font-medium hover:bg-navy/90 disabled:opacity-50 flex items-center justify-between gap-2 min-w-0"
          >
            <span className="truncate">{busy === p.key ? 'Redirecting…' : `Subscribe · ${p.label}`}</span>
            <span className="text-xs opacity-80 flex-shrink-0">{p.price}</span>
          </button>
        ))}
      </div>
      {error && <div className="border border-red-200 bg-red-50 text-red-800 text-sm rounded p-2">{error}</div>}
    </section>
  );
}

function L({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block min-w-0">
      <span className="text-sm font-medium text-navy">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
